import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  Upload,
  Avatar,
  Space,
  Row,
  Col,
  Typography,
  message,
  Divider,
  Spin,
  Select
} from 'antd';
import {
  UploadOutlined,
  DeleteOutlined,
  SaveOutlined,
  ShopOutlined,
  PictureOutlined
} from '@ant-design/icons';
import {
  getCompanySettings,
  updateCompanySettings,
  uploadCompanyLogo,
  deleteCompanyLogo,
  getLogoUrl,
  uploadCompanySignature,
  deleteCompanySignature,
  getSignatureUrl,
  uploadCompanyStamp,
  deleteCompanyStamp,
  getStampUrl
} from '../../services/settingsService';

const { Title, Text } = Typography;
const { TextArea } = Input;
const API_URL = process.env.REACT_APP_API_URL;

const CompanySettingsPage = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [logoLoading, setLogoLoading] = useState(false);
  const [signatureLoading, setSignatureLoading] = useState(false);
  const [stampLoading, setStampLoading] = useState(false);
  const [settings, setSettings] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);
  const [signatureUrl, setSignatureUrl] = useState(null);
  const [stampUrl, setStampUrl] = useState(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await getCompanySettings();
      //console.log('company data:', response);
      if (response.data.success) {
        const data = response.data.data;
        setSettings(data);
        form.setFieldsValue(data);
        setLogoUrl(data.logo_path ? getLogoUrl(`${data.logo_path}`) : null);
        setSignatureUrl(data.signature_path ? getSignatureUrl(`${data.signature_path}`) : null);
        setStampUrl(data.stamp_path ? getStampUrl(`${data.stamp_path}`) : null);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      message.error('ไม่สามารถโหลดข้อมูลบริษัทได้');
    } finally {
      setLoading(false);
      //console.log('image url:', logoUrl);
    }
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      const response = await updateCompanySettings(values);

      if (response.data.success) {
        message.success('บันทึกข้อมูลบริษัทเรียบร้อยแล้ว');
        setSettings(response.data.data);
      }
    } catch (error) {
      console.error('Error updating settings:', error);
      message.error('ไม่สามารถบันทึกข้อมูลได้');
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = async (file) => {
    try {
      setLogoLoading(true);
      const response = await uploadCompanyLogo(file);

      if (response.data.success) {
        message.success('อัปโหลดโลโก้เรียบร้อยแล้ว');
        setLogoUrl(response.data.data.logo_url);
        await fetchSettings(); // Refresh settings
      }
    } catch (error) {
      console.error('Error uploading logo:', error);
      message.error('ไม่สามารถอัปโหลดโลโก้ได้');
    } finally {
      setLogoLoading(false);
    }

    return false; // Prevent default upload behavior
  };

  const handleDeleteLogo = async () => {
    try {
      setLogoLoading(true);
      const response = await deleteCompanyLogo();

      if (response.data.success) {
        message.success('ลบโลโก้เรียบร้อยแล้ว');
        setLogoUrl(null);
        await fetchSettings(); // Refresh settings
      }
    } catch (error) {
      console.error('Error deleting logo:', error);
      message.error('ไม่สามารถลบโลโก้ได้');
    } finally {
      setLogoLoading(false);
    }
  };

  const handleSignatureUpload = async (file) => {
    try {
      setSignatureLoading(true);
      const response = await uploadCompanySignature(file);

      if (response.data.success) {
        message.success('อัปโหลดรูปลายเซ็นต์เรียบร้อยแล้ว');
        setSignatureUrl(response.data.data.signature_url);
        await fetchSettings(); // Refresh settings
      }
    } catch (error) {
      console.error('Error uploading signature:', error);
      message.error('ไม่สามารถอัปโหลดรูปลายเซ็นต์ได้');
    } finally {
      setSignatureLoading(false);
    }

    return false; // Prevent default upload behavior
  };

  const handleDeleteSignature = async () => {
    try {
      setSignatureLoading(true);
      const response = await deleteCompanySignature();

      if (response.data.success) {
        message.success('ลบรูปลายเซ็นต์เรียบร้อยแล้ว');
        setSignatureUrl(null);
        await fetchSettings(); // Refresh settings
      }
    } catch (error) {
      console.error('Error deleting signature:', error);
      message.error('ไม่สามารถลบรูปลายเซ็นต์ได้');
    } finally {
      setSignatureLoading(false);
    }
  };

  const handleStampUpload = async (file) => {
    try {
      setStampLoading(true);
      const response = await uploadCompanyStamp(file);

      if (response.data.success) {
        message.success('อัปโหลดรูปตราประทับเรียบร้อยแล้ว');
        setStampUrl(response.data.data.stamp_url);
        await fetchSettings(); // Refresh settings
      }
    } catch (error) {
      console.error('Error uploading stamp:', error);
      message.error('ไม่สามารถอัปโหลดรูปตราประทับได้');
    } finally {
      setStampLoading(false);
    }

    return false; // Prevent default upload behavior
  };

  const handleDeleteStamp = async () => {
    try {
      setStampLoading(true);
      const response = await deleteCompanyStamp();

      if (response.data.success) {
        message.success('ลบรูปตราประทับเรียบร้อยแล้ว');
        setStampUrl(null);
        await fetchSettings(); // Refresh settings
      }
    } catch (error) {
      console.error('Error deleting stamp:', error);
      message.error('ไม่สามารถลบรูปตราประทับได้');
    } finally {
      setStampLoading(false);
    }
  };

  const beforeUpload = (file) => {
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      message.error('รองรับเฉพาะไฟล์รูปภาพเท่านั้น');
      return false;
    }

    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      message.error('ขนาดไฟล์ต้องไม่เกิน 5MB');
      return false;
    }

    return true;
  };

  const beforeImageUpload = (file) => {
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      message.error('รองรับเฉพาะไฟล์รูปภาพเท่านั้น');
      return false;
    }

    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      message.error('ขนาดไฟล์ต้องไม่เกิน 2MB');
      return false;
    }

    return true;
  };

  if (loading && !settings) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>
        <ShopOutlined /> การตั้งค่าบริษัท
      </Title>
      <Text type="secondary">จัดการข้อมูลพื้นฐานของบริษัท ที่จะใช้ในใบเสนอราคา ใบแจ้งหนี้ และเอกสารต่างๆ</Text>

      <Row gutter={24} style={{ marginTop: '24px' }}>
        {/* Company Logo Section */}
        <Col xs={24} lg={8}>
          <Card title={<><PictureOutlined /> โลโก้บริษัท</>}>
            <div style={{ textAlign: 'center' }}>
              <Avatar
                size={120}
                src={logoUrl}
                icon={<ShopOutlined />}
                style={{
                  marginBottom: '16px',
                  border: '2px dashed #d9d9d9'
                }}
              />

              <div>
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  <Upload
                    beforeUpload={beforeUpload}
                    customRequest={({ file }) => handleLogoUpload(file)}
                    showUploadList={false}
                    accept="image/*"
                  >
                    <Button
                      icon={<UploadOutlined />}
                      loading={logoLoading}
                      block
                    >
                      อัปโหลดโลโก้
                    </Button>
                  </Upload>

                  {logoUrl && (
                    <Button
                      danger
                      icon={<DeleteOutlined />}
                      onClick={handleDeleteLogo}
                      loading={logoLoading}
                      block
                    >
                      ลบโลโก้
                    </Button>
                  )}
                </Space>

                <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: '8px' }}>
                  รองรับไฟล์: JPG, PNG, GIF, WebP<br />
                  ขนาดไม่เกิน 5MB
                </Text>
              </div>
            </div>
          </Card>
        </Col>

        {/* Company Signature Section */}
        <Col xs={24} lg={8}>
          <Card title="รูปลายเซ็นต์">
            <div style={{ textAlign: 'center' }}>
              <Avatar
                size={120}
                src={signatureUrl}
                icon={<PictureOutlined />}
                style={{
                  marginBottom: '16px',
                  border: '2px dashed #d9d9d9'
                }}
              />

              <div>
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  <Upload
                    beforeUpload={beforeImageUpload}
                    customRequest={({ file }) => handleSignatureUpload(file)}
                    showUploadList={false}
                    accept="image/*"
                  >
                    <Button
                      icon={<UploadOutlined />}
                      loading={signatureLoading}
                      block
                    >
                      อัปโหลดลายเซ็นต์
                    </Button>
                  </Upload>

                  {signatureUrl && (
                    <Button
                      danger
                      icon={<DeleteOutlined />}
                      onClick={handleDeleteSignature}
                      loading={signatureLoading}
                      block
                    >
                      ลบลายเซ็นต์
                    </Button>
                  )}
                </Space>

                <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: '8px' }}>
                  รองรับไฟล์: JPG, JPEG, PNG<br />
                  ขนาดไม่เกิน 2MB
                </Text>
              </div>
            </div>
          </Card>
        </Col>

        {/* Company Stamp Section */}
        <Col xs={24} lg={8}>
          <Card title="รูปตราประทับ">
            <div style={{ textAlign: 'center' }}>
              <Avatar
                size={120}
                src={stampUrl}
                icon={<PictureOutlined />}
                style={{
                  marginBottom: '16px',
                  border: '2px dashed #d9d9d9'
                }}
              />

              <div>
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  <Upload
                    beforeUpload={beforeImageUpload}
                    customRequest={({ file }) => handleStampUpload(file)}
                    showUploadList={false}
                    accept="image/*"
                  >
                    <Button
                      icon={<UploadOutlined />}
                      loading={stampLoading}
                      block
                    >
                      อัปโหลดตราประทับ
                    </Button>
                  </Upload>

                  {stampUrl && (
                    <Button
                      danger
                      icon={<DeleteOutlined />}
                      onClick={handleDeleteStamp}
                      loading={stampLoading}
                      block
                    >
                      ลบตราประทับ
                    </Button>
                  )}
                </Space>

                <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: '8px' }}>
                  รองรับไฟล์: JPG, JPEG, PNG<br />
                  ขนาดไม่เกิน 2MB
                </Text>
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Company Information Section - Full Width */}
      <Row style={{ marginTop: '24px' }}>
        <Col xs={24}>
          <Card title="ข้อมูลบริษัท">
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
              initialValues={settings}
            >
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item
                    label="ชื่อบริษัท"
                    name="company_name"
                    rules={[{ required: true, message: 'กรุณากรอกชื่อบริษัท' }]}
                  >
                    <Input placeholder="ชื่อบริษัทของคุณ" />
                  </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                  <Form.Item
                    label="เลขประจำตัวผู้เสียภาษี"
                    name="tax_id"
                  >
                    <Input placeholder="เลขประจำตัวผู้เสียภาษี" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item
                    label="สาขา"
                    name="branch_name"
                    rules={[{ required: true, message: 'กรุณาเลือกหรือกรอกชื่อสาขา' }]}
                  >
                    <Select
                      mode="tags"
                      maxTagCount={1}
                      maxCount={1}
                      placeholder="เลือกหรือพิมพ์ชื่อสาขา"
                      style={{ width: '100%' }}
                      options={[
                        { value: '-', label: 'ไม่ระบุ' },
                        { value: 'สำนักงานใหญ่', label: 'สำนักงานใหญ่' },
                        { value: 'สาขา 1', label: 'สาขา 1' },
                        { value: 'สาขา 2', label: 'สาขา 2' },
                        { value: 'สาขา 3', label: 'สาขา 3' },
                      ]}
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                  <Form.Item
                    label="ผู้มีอำนาจลงนาม"
                    name="signature_name"
                  >
                    <Input placeholder="ชื่อผู้มีอำนาจลงนามใบเสร็จ/ใบแจ้งหนี้" />
                  </Form.Item>
                </Col>
              </Row>


              <Form.Item
                label="ที่อยู่บริษัท"
                name="address"
              >
                <TextArea
                  placeholder="ที่อยู่บริษัทของคุณ"
                  rows={3}
                />
              </Form.Item>

              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item
                    label="หมายเลขโทรศัพท์"
                    name="phone"
                  >
                    <Input placeholder="หมายเลขโทรศัพท์บริษัท" />
                  </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                  <Form.Item
                    label="อีเมล"
                    name="email"
                    rules={[
                      { type: 'email', message: 'รูปแบบอีเมลไม่ถูกต้อง' }
                    ]}
                  >
                    <Input placeholder="info@company.com" />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                label="เว็บไซต์"
                name="website"
                rules={[
                  { type: 'url', message: 'รูปแบบเว็บไซต์ไม่ถูกต้อง' }
                ]}
              >
                <Input placeholder="https://www.company.com" />
              </Form.Item>

              <Divider />

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<SaveOutlined />}
                  loading={loading}
                  size="large"
                >
                  บันทึกข้อมูล
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </Col>
      </Row>
    </div >
  );
};

export default CompanySettingsPage;